import { useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PageBar } from '@/app/layout/page-bar'
import { CategoryPills } from '@/features/templates/components/category-pills'
import { CreateTemplateDialog } from '@/features/templates/components/create-template-dialog'
import { TemplateCard } from '@/features/templates/components/template-card'
import {
  filterTemplates,
  groupTemplates,
  usedCategories,
} from '@/features/templates/gallery'
import { useTemplates } from '@/features/templates/queries'
import type { TemplateCategory, TemplateListItem } from '@/api/types'
import { useTenantWorkspace } from '@/features/workspace/use-tenant-workspace'
import { SearchBox } from '@/shared/components/search-box'
import { Button } from '@/shared/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'

const ROUTE = '/shell/templates'

/** The platform's templates and the company's own, to start a database or dashboard from. */
export function TemplatesPage() {
  const { t } = useTranslation()
  const { workspace } = useTenantWorkspace()
  const [creating, setCreating] = useState(false)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBar />
      <div className="min-h-0 flex-1 space-y-4 overflow-auto p-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">{t('templates.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('templates.description')}</p>
          </div>
          <Button onClick={() => setCreating(true)} className="on-surface">
            <Plus />
            {t('templates.create.button')}
          </Button>
        </header>
        <CreateTemplateDialog open={creating} onOpenChange={setCreating} />
        {workspace === undefined ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <Gallery workspaceId={workspace.id} />
        )}
      </div>
    </div>
  )
}

function Gallery({ workspaceId }: { workspaceId: number }) {
  const { t } = useTranslation()
  const { category = 'all', search = '' } = useSearch({ from: ROUTE })
  const navigate = useNavigate()
  const { data, isPending } = useTemplates(workspaceId)

  function go(changes: { category?: string; search?: string }) {
    const next = { category, search, ...changes }
    void navigate({
      to: '/templates',
      search: {
        category: next.category === 'all' ? undefined : next.category,
        search: next.search === '' ? undefined : next.search,
      },
      replace: true,
    })
  }

  if (isPending || data === undefined) return <Skeleton className="h-64 w-full" />

  const categories = usedCategories(data.templates, data.categories)
  const shown = filterTemplates(data.templates, category, search)

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CategoryPills
          categories={categories}
          archived={
            category === 'archived' ||
            data.templates.some((template) => template.archived)
          }
          value={category}
          onChange={(value) => go({ category: value })}
        />
        <SearchBox
          term={search}
          label={t('templates.search')}
          className="h-8 w-56 pr-7 pl-7 bg-background on-surface"
          onSearch={(term) => go({ search: term })}
        />
      </div>

      {shown.length === 0 ? (
        <Empty className="mt-6">
          <EmptyHeader>
            {category === 'company' && search === '' ? (
              <>
                <EmptyTitle>{t('templates.companyEmptyTitle')}</EmptyTitle>
                <EmptyDescription>
                  {t('templates.companyEmptyDescription')}
                </EmptyDescription>
              </>
            ) : (
              <>
                <EmptyTitle>{t('templates.emptyTitle')}</EmptyTitle>
                <EmptyDescription>{t('templates.emptyDescription')}</EmptyDescription>
              </>
            )}
          </EmptyHeader>
        </Empty>
      ) : category === 'all' && search === '' ? (
        groupTemplates(data.templates, [
          { id: 'company', label: t('templates.company') },
          ...categories,
        ]).map((group) => (
          <section
            key={group.id}
            aria-labelledby={`templates-${group.id}`}
            className="space-y-3 pt-2"
          >
            <h2 id={`templates-${group.id}`} className="text-lg font-medium">
              {group.label}
            </h2>
            <TemplateGrid
              label={group.label}
              templates={group.templates}
              categories={data.categories}
            />
          </section>
        ))
      ) : (
        <TemplateGrid
          label={t('templates.title')}
          templates={shown}
          categories={data.categories}
        />
      )}
    </>
  )
}

function TemplateGrid({
  label,
  templates,
  categories,
}: {
  label: string
  templates: readonly TemplateListItem[]
  categories: readonly TemplateCategory[]
}) {
  return (
    <ul
      aria-label={label}
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      {templates.map((template) => (
        <li key={template.id}>
          <TemplateCard template={template} categories={categories} />
        </li>
      ))}
    </ul>
  )
}
